import { useTranslation } from 'react-i18next'
import { HealthCallAppointment } from '../api/ester-b2b-api/private/healthCalls/types'
import styled from 'styled-components'
import { theme } from '../theme/theme'
import { BodyLarge, BodyLargeMedium, BodySmall, BodySmallMedium } from './styles/Typography'
import { CheckRing, Info, User, Calendar, Pin } from './icons'
import { OnClickButton } from './buttons/onclick/OnClickButton'
import { formatDisplayStartTime } from '../utils/dates'
import { Modal } from './modals/Modal'
import { useState } from 'react'
import { usePatchCancelHealthCallQuery } from '../api/ester-b2b-api/private/healthCalls/healthCallQueries'
import { useHealthCallStore } from '../state/store'
import { isWithin24Hours } from '../utils/timeSlotCalendar'
import { useGetSpecialistById } from '../api/contentful/contentfulQueries'

interface HealthCallBookingDetailsProps {
  appointment: HealthCallAppointment
}

export const HealthCallBookingDetails = ({ appointment }: HealthCallBookingDetailsProps) => {
  const { t, i18n } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const [isError, setIsError] = useState(false)
  const [cancelSuccess, setCancelSuccess] = useState(false)
  const { resetState } = useHealthCallStore()
  const { data: specialistData } = useGetSpecialistById(appointment.specialistId || '')
  const specialist = specialistData?.items[0]?.fields

  const cancelHealthCallMutation = usePatchCancelHealthCallQuery({
    onError: () => {
      setIsError(true)
    },
    onSuccess: () => {
      setCancelSuccess(true)
      resetState()
    },
  })

  const onClickCancel = () => {
    setShowModal(true)
  }
  return (
    <Container>
      <InfoContainer>
        <TextWrapper>
          <Calendar />
          <BodySmall>{formatDisplayStartTime(appointment, i18n.language, t)}</BodySmall>
        </TextWrapper>
        <TextWrapper>
          <User size={24} />
          <BodySmall>{`${specialist?.firstName || ''} ${specialist?.lastName || ''}, ${specialist?.jobTitlePortal || specialist?.jobTitle || ''}`}</BodySmall>
        </TextWrapper>
        <TextWrapper>
          <Pin />
          <BodySmallMedium>{t('employee.healthCall.bookingInfo')}</BodySmallMedium>
        </TextWrapper>
      </InfoContainer>
      <InfoContainer>
        <InfoWrapper>
          <Info size={24} color={theme.color.plum} />
          <BodySmall>{t('employee.healthCall.cancelWarning')}</BodySmall>
        </InfoWrapper>
      </InfoContainer>
      <ButtonWrapper>
        <OnClickButton onClick={onClickCancel} text={t('employee.healthCall.cancelBooking')} secondary />
      </ButtonWrapper>
      {showModal && (
        <Modal
          onClose={() => {
            if (cancelSuccess) {
              setShowModal(false)
            } else {
              setShowModal(false)
              setIsError(false)
              setCancelSuccess(false)
            }
          }}
        >
          <ModalContent>
            {cancelSuccess ? (
              <>
                <CheckRing size={78} color={theme.color.green} />
                <BodyLargeMedium>{t('employee.healthCall.cancelSuccessTitle')}</BodyLargeMedium>
                <BodySmall>{t('employee.healthCall.cancelSuccessMessage')}</BodySmall>
                <ButtonWrapper>
                  <OnClickButton
                    onClick={() => {
                      setShowModal(false)
                    }}
                    text={t('common.form.close')}
                  />
                </ButtonWrapper>
              </>
            ) : (
              <>
                <Info size={78} color={theme.color.plum} />
                <BodyLargeMedium>{t('employee.healthCall.cancelConfirmation')}</BodyLargeMedium>
                <TextWrapper>
                  <Calendar />
                  <BodySmall>{formatDisplayStartTime(appointment, i18n.language, t)}</BodySmall>
                </TextWrapper>
                {isWithin24Hours(appointment) && <BodyLarge>{t('employee.healthCall.cancelWarning')}</BodyLarge>}
                <ModalButtonWrapper>
                  {isError && <ErrorMessage>{t('employee.healthCall.cancelError')}</ErrorMessage>}
                  <ButtonWrapper>
                    <OnClickButton
                      onClick={() => cancelHealthCallMutation.mutate(appointment.id)}
                      text={t('employee.healthCall.cancelBooking')}
                      loading={cancelHealthCallMutation.isPending}
                    />
                  </ButtonWrapper>
                </ModalButtonWrapper>
              </>
            )}
          </ModalContent>
        </Modal>
      )}
    </Container>
  )
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
  padding: ${theme.spacing.medium}px;
  max-width: 500px;
  align-items: center;
  text-align: center;
  @media screen and (max-width: ${theme.breakpoint.large}px) {
    max-width: 250px;
    padding: 0;
  }
`
const ModalButtonWrapper = styled.div`
  margin-top: ${theme.spacing.xlarge}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
  justify-content: center;
`
const ButtonWrapper = styled.div`
  margin-top: auto;
  display: flex;
  width: 100%;
  justify-content: center;
`

const InfoWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.small}px;
  color: ${theme.color.plum};
  > svg {
    flex-shrink: 0;
  }
`
const TextWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${theme.spacing.small}px;
  > svg {
    flex-shrink: 0;
  }
`
const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small}px;
  padding-bottom: ${theme.spacing.medium}px;
  border-bottom: 1px solid ${theme.color.grey2};
  width: 100%;
`
const Container = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  background-color: ${theme.color.white};
  min-height: 300px;
  padding: ${theme.spacing.large}px;
  border-radius: ${theme.spacing.mediumLarge}px;
  gap: ${theme.spacing.medium}px;
`
const ErrorMessage = styled.div`
  color: ${theme.color.red};
`
